import { CONTACT_CATEGORIES, CONTACT_CATEGORY_LABELS, type ContactCategory, type ContactTypeItem } from "@nmt/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { del, errorMessage, get, patch, post, put } from "../../api";
import { Alert, Button, Empty, Field, Loading, PageHeader, Panel, PanelHeader, SelectField } from "../../components/ui";
import { useSession } from "../../session";

const categoryHelp: Record<ContactCategory, string> = {
  customer: "Can have jobs. Becomes a customer in QuickBooks.",
  subcontractor: "Gets work orders. Becomes a vendor in QuickBooks; needs a W-9 and insurance.",
  vendor: "Material suppliers and other businesses you pay. Becomes a vendor in QuickBooks.",
  other: "Adjusters, family members, realtors… Stays in the CRM only.",
};

/** Settings → Contact types: the types a contact can be, and what each means to QuickBooks. */
export function ContactTypesSettings() {
  const { refresh } = useSession();
  const types = useQuery({ queryKey: ["contact-types"], queryFn: () => get<ContactTypeItem[]>("/api/contact-types") });
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [category, setCategory] = useState<ContactCategory>("other");

  const done = { onSuccess: async () => {
    setError(null);
    await types.refetch();
    await refresh();
  }, onError: (e: unknown) => setError(errorMessage(e)) };
  const add = useMutation({ mutationFn: () => post("/api/contact-types", { name, category }), ...done, onSuccess: async () => {
    setName("");
    await done.onSuccess();
  } });
  const save = useMutation({ mutationFn: (t: { id: string; name: string; category: ContactCategory }) => patch(`/api/contact-types/${t.id}`, { name: t.name, category: t.category }), ...done });
  const remove = useMutation({ mutationFn: (id: string) => del(`/api/contact-types/${id}`), ...done });
  const order = useMutation({ mutationFn: (ids: string[]) => put("/api/contact-types/order", { ids }), ...done });

  const list = types.data ?? [];
  const move = (i: number, by: number) => {
    const ids = list.map((t) => t.id);
    const [x] = ids.splice(i, 1);
    ids.splice(i + by, 0, x!);
    order.mutate(ids);
  };

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Contact types"
        description="Every contact has one type. The category decides what it means: customers can have jobs and become QuickBooks customers; subcontractors and vendors become QuickBooks vendors."
      />
      {error && <Alert>{error}</Alert>}
      <Panel>
        <PanelHeader title="Types" description="A type someone has can't be deleted. Website leads get the first customer type." />
        {types.isLoading ? (
          <div className="px-5">
            <Loading />
          </div>
        ) : !list.length ? (
          <Empty>No contact types.</Empty>
        ) : (
          <ul>
            {list.map((t, i) => (
              <TypeRow
                key={`${t.id}-${t.name}-${t.category}`}
                type={t}
                first={i === 0}
                last={i === list.length - 1}
                onSave={(next) => save.mutate({ id: t.id, ...next })}
                onDelete={() => {
                  if (window.confirm(`Delete the contact type ${t.name}?`)) remove.mutate(t.id);
                }}
                onMove={(by) => move(i, by)}
              />
            ))}
          </ul>
        )}
      </Panel>
      <Panel>
        <PanelHeader title="Add a type" />
        <form
          className="grid gap-3 px-5 py-5 sm:grid-cols-[1fr_220px_200px] sm:items-start"
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim()) add.mutate();
          }}
        >
          <Field label="Name" placeholder="Roofing crew" value={name} onChange={(e) => setName(e.target.value)} />
          <SelectField label="Category" value={category} onChange={(e) => setCategory(e.target.value as ContactCategory)} hint={categoryHelp[category]}>
            {CONTACT_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {CONTACT_CATEGORY_LABELS[c].label}
              </option>
            ))}
          </SelectField>
          <Button variant="primary" type="submit" busy={add.isPending} disabled={!name.trim()} className="justify-self-start sm:mt-6">
            Add
          </Button>
        </form>
      </Panel>
    </div>
  );
}

function TypeRow({
  type,
  first,
  last,
  onSave,
  onDelete,
  onMove,
}: {
  type: ContactTypeItem;
  first: boolean;
  last: boolean;
  onSave: (t: { name: string; category: ContactCategory }) => void;
  onDelete: () => void;
  onMove: (by: number) => void;
}) {
  const [name, setName] = useState(type.name);
  const [category, setCategory] = useState(type.category);
  const changed = name.trim() !== type.name || category !== type.category;
  return (
    <li className="grid gap-3 border-b border-mist px-5 py-4 last:border-0 sm:grid-cols-[1fr_220px_200px] sm:items-start">
      <Field label="Name" value={name} onChange={(e) => setName(e.target.value)} hint={`${type.contactCount} contact${type.contactCount === 1 ? "" : "s"}`} />
      <SelectField label="Category" value={category} onChange={(e) => setCategory(e.target.value as ContactCategory)} hint={CONTACT_CATEGORY_LABELS[category].quickbooks}>
        {CONTACT_CATEGORIES.map((c) => (
          <option key={c} value={c}>
            {CONTACT_CATEGORY_LABELS[c].label}
          </option>
        ))}
      </SelectField>
      <div className="flex flex-wrap gap-1 sm:mt-6">
        {changed && (
          <Button variant="primary" className="px-3 py-1.5" onClick={() => onSave({ name: name.trim(), category })} disabled={!name.trim()}>
            Save
          </Button>
        )}
        <Button className="px-2 py-1.5" onClick={() => onMove(-1)} disabled={first} aria-label={`Move ${type.name} up`}>
          ↑
        </Button>
        <Button className="px-2 py-1.5" onClick={() => onMove(1)} disabled={last} aria-label={`Move ${type.name} down`}>
          ↓
        </Button>
        {type.contactCount === 0 && (
          <Button variant="quiet" className="px-3 py-1.5" onClick={onDelete}>
            Delete
          </Button>
        )}
      </div>
    </li>
  );
}
